import { useMemo } from 'react';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import {
  type ProductDetailsContent,
  type ProductDetailsMappedContent,
} from '../../ProductDetails.types.ts';

// ─────────────────────────────────────────────────────────────────────────────

export const useProductDetailsContent = (params: Params) => {
  const { baseProductSlug } = params;

  // ─── Remote Data ─────────────────────────────────────────────────────

  const searchParams = useMemo(
    () => ({ 'fields.baseProductSlug': baseProductSlug }),
    [baseProductSlug],
  );

  const { data, fetching } =
    useContentfulContentTypeEntry<ProductDetailsContent>({
      contentType: PRODUCT_DETAILS_CONTENT_TYPE,
      searchParams,
    });

  // ─── Derived Data ────────────────────────────────────────────────────

  const superIngredients = useMemo(() => {
    const entries = data?.fields?.superIngredients ?? [];

    return entries.map((ingredient) => ingredient.fields);
  }, [data?.fields?.superIngredients]);

  const mappedContent = useMemo<ProductDetailsMappedContent>(
    () => ({
      subheading: data?.fields?.subheading,
      description: data?.fields?.description,
      superIngredients,
      carousel: data?.fields?.carousel ?? [],
      mobileHeaderBackgroundColor: data?.fields?.mobileHeaderBackgroundColor,
      mobileHeaderBackgroundImage: data?.fields?.mobileHeaderBackgroundImage,
      mobileHeaderForegroundColor: data?.fields?.mobileHeaderForegroundColor,
      nutritionistPhoto: data?.fields?.nutritionistPhoto,
      nutritionistTitle: data?.fields?.nutritionistTitle,
      nutritionistLogo: data?.fields?.nutritionistLogo,
      nutritionistLogoWidth: data?.fields?.nutritionistLogoWidth,
      nutritionistLogoHeight: data?.fields?.nutritionistLogoHeight,
    }),
    [
      data?.fields?.subheading,
      data?.fields?.description,
      data?.fields?.carousel,
      data?.fields?.mobileHeaderBackgroundColor,
      data?.fields?.mobileHeaderBackgroundImage,
      data?.fields?.mobileHeaderForegroundColor,
      data?.fields?.nutritionistPhoto,
      data?.fields?.nutritionistTitle,
      data?.fields?.nutritionistLogo,
      data?.fields?.nutritionistLogoWidth,
      data?.fields?.nutritionistLogoHeight,
      superIngredients,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    fetching,
    mappedContent,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PRODUCT_DETAILS_CONTENT_TYPE = 'productDetails';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProductSlug: string;
};
